import React, { useState } from 'react';
import Button from '../../components/Button';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { useGetUser } from '../../user/useAllUsers';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import {
  CommentActions,
  CommentAuthor,
  CommentContainer,
  CommentLeft,
  CommentMeta,
  CommentRight,
} from './Comment';
import CommentPreview from './CommentPreview';
import CommentTimestamp from './CommentTimestamp';
import getFirstName from '../../utils/text/getFirstName';

const MAX_LINE_COUNT = 4;

const COMMENT_FRAGMENT = gql(`
  fragment HistoricalComment_comment on Comment {
    id
    content
    author {
      ...useGetUser_user
    }
    insertedAt
    updatedAt
  }
`);

interface HistoricalCommentProps {
  comment: DocumentType<typeof COMMENT_FRAGMENT>;
}

const HistoricalComment = ({ comment }: HistoricalCommentProps) => {
  const [isShowingMore, setIsShowingMore] = useState(false);
  const [canBeClamped, setCanBeClamped] = useState<boolean | null>(null);
  const getUser = useGetUser();
  const author = getUser(comment.author);

  const toggleIsShowingMore = () => {
    setIsShowingMore((val) => !val);
  };

  if (!comment) return null;
  return (
    <CommentContainer>
      <CommentLeft>
        <MagneticWallItemAvatar user={author} />
      </CommentLeft>
      <CommentRight>
        <CommentMeta>
          <CommentAuthor>{getFirstName(author.name)}</CommentAuthor>
          <CommentTimestamp
            insertedAt={comment.insertedAt}
            updatedAt={comment.updatedAt}
          />
        </CommentMeta>
        <CommentPreview
          content={comment.content}
          maxLines={MAX_LINE_COUNT}
          disableLineClamp={isShowingMore}
          onCanBeClampedChange={setCanBeClamped}
        />
        {canBeClamped && (
          <CommentActions>
            <Button variant="link" onClick={toggleIsShowingMore}>
              {isShowingMore ? 'Show less' : 'Show more'}
            </Button>
          </CommentActions>
        )}
      </CommentRight>
    </CommentContainer>
  );
};

export default HistoricalComment;
